/**
 * Calculation Transparency Component
 * Shows detailed calculation steps and data sources
 * 
 * @version 1.0.0
 * @date 2025-10-12
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

import { MARKET_COMPARISON_RELEASED } from '../lib/marketComparisonGate';

export function CalculationTransparency({ valuationResult }) {
  const [expandedMethod, setExpandedMethod] = useState(null);
  
  if (!valuationResult) return null;

  const toggleMethod = (method) => {
    setExpandedMethod(expandedMethod === method ? null : method);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Info className="w-5 h-5 mr-2 text-purple-600" />
        Berechnungsdetails & Transparenz
      </h2>

      {/* Data Sources Overview */}
      <DataSourcesOverview sources={valuationResult.data_lineage?.sources} />

      {/* Errors and Warnings */}
      {(valuationResult.errors?.length > 0 || valuationResult.warnings?.length > 0) && (
        <div className="mt-4 space-y-2">
          {valuationResult.errors?.map((error, idx) => (
            <div key={`error-${idx}`} className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          ))}
          {valuationResult.warnings?.map((warning, idx) => (
            <div key={`warning-${idx}`} className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{warning}</p>
            </div>
          ))}
        </div>
      )}

      {/* Income Approach Details */}
      {valuationResult.methods?.income_approach && (
        <MethodDetails
          method="income_approach"
          title={MARKET_COMPARISON_RELEASED ? 'Ertragswertverfahren' : 'Szenario auf dokumentierter Mietbasis'}
          data={valuationResult.methods.income_approach}
          expanded={expandedMethod === 'income_approach'}
          onToggle={() => toggleMethod('income_approach')}
        />
      )}

      {/* Comparable Sales Details */}
      {valuationResult.methods?.comparable_sales && (
        <MethodDetails
          method="comparable_sales"
          title="Vergleichswertverfahren"
          status={MARKET_COMPARISON_RELEASED ? undefined : 'Nicht als Marktvergleich freigegeben'}
          data={valuationResult.methods.comparable_sales}
          expanded={expandedMethod === 'comparable_sales'}
          onToggle={() => toggleMethod('comparable_sales')}
        />
      )}

      {/* BORIS Land Value */}
      {valuationResult.boris_land_value && (
        <BorisLandValue data={valuationResult.boris_land_value} />
      )}
    </div>
  );
}

function DataSourcesOverview({ sources }) {
  if (!sources || sources.length === 0) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'unavailable':
        return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    }
  };

  const getSourceLabel = (name) => {
    const labels = {
      'tenant_rent_data': 'Mietdaten',
      'location_score': 'Lage-Score',
      'property_suggestions': 'Vergleichsobjekte',
      'market_data': 'Marktdaten',
      'boris_land_value': 'Regionaler Bodenwert-Proxy (nicht amtlich)',
      'vpi_service': 'VPI Daten'
    };
    return labels[name] || name;
  };

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        📊 Datenquellen
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {sources.map((source, idx) => (
          <div 
            key={idx}
            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600"
          >
            <div className="flex items-center gap-2">
              {source.name === 'boris_land_value'
                ? <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                : getStatusIcon(source.status)}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {getSourceLabel(source.name)}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {source.count !== undefined && `${source.count} Einträge`}
              {source.value !== undefined && `${source.value}`}
              {source.name === 'boris_land_value' && (
                <span className="text-amber-600 dark:text-amber-400"> · nicht amtlich verifiziert</span>
              )}
              {source.reason && (
                <span className="text-red-600 dark:text-red-400">{source.reason}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MethodDetails({ method, title, data, expanded, onToggle, status }) {
  if (!data) return null;

  const hasError = !!data.error;
  const hasSteps = data.calculation_steps && data.calculation_steps.length > 0;

  return (
    <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {status && (
            <span className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded">
              {status}
            </span>
          )}
          {hasError ? (
            data.provenance_gap ? (
              <span className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded">
                Datenlücke
              </span>
            ) : (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded">
                Fehler
              </span>
            )
          ) : (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
              ✓ {data.value?.toLocaleString('de-DE')} €
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          {hasError ? (
            <div className="space-y-2">
              <p className="text-red-800 dark:text-red-200 font-medium">{data.error}</p>
              {data.message && (
                <p className="text-sm text-gray-700 dark:text-gray-300">{data.message}</p>
              )}
              {data.recommendation && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 <strong>Empfehlung:</strong> {data.recommendation}
                  </p>
                </div>
              )}
            </div>
          ) : hasSteps ? (
            <CalculationSteps steps={data.calculation_steps} />
          ) : (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Keine detaillierten Berechnungsschritte verfügbar.
            </div>
          )}

          {/* Details Summary */}
          {data.details && !hasError && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Zusammenfassung
              </h4>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {Object.entries(data.details).map(([key, value]) => {
                  // Skip objects, arrays, null, undefined
                  if (value === null || value === undefined || typeof value === 'object') return null;
                  
                  // Convert value to string/number safely
                  let displayValue;
                  if (typeof value === 'number' && value > 100) {
                    displayValue = value.toLocaleString('de-DE') + (key.includes('price') || key.includes('value') || key.includes('rent') || key.includes('income') || key.includes('expenses') ? ' €' : '');
                  } else if (typeof value === 'boolean') {
                    displayValue = value ? 'Ja' : 'Nein';
                  } else {
                    displayValue = String(value);
                  }
                  
                  return (
                    <React.Fragment key={key}>
                      <dt className="text-gray-600 dark:text-gray-400 capitalize">
                        {key.replace(/_/g, ' ')}:
                      </dt>
                      <dd className="text-gray-900 dark:text-white font-medium">
                        {displayValue}
                      </dd>
                    </React.Fragment>
                  );
                })}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CalculationSteps({ steps }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        🔢 Berechnungsschritte
      </h4>
      {steps.map((step, idx) => (
        <div 
          key={idx}
          className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
        >
          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full flex items-center justify-center font-bold text-sm">
            {step.step}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {step.name}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-mono mb-1">
              {step.formula}
            </p>
            <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
              = {step.display || (typeof step.result === 'number' && step.result > 100 
                ? step.result.toLocaleString('de-DE') 
                : step.result)} {step.unit}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function BorisLandValue({ data }) {
  if (!data || !data.value) return null;

  return (
    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        Regionaler Bodenwert-Proxy · nicht amtlich verifiziert
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-blue-700 dark:text-blue-300">Wert</p>
          <p className="font-bold text-blue-900 dark:text-blue-100">
            {data.value} €/m²
          </p>
        </div>
        <div>
          <p className="text-blue-700 dark:text-blue-300">Stadt</p>
          <p className="font-bold text-blue-900 dark:text-blue-100">{data.city}</p>
        </div>
        <div>
          <p className="text-blue-700 dark:text-blue-300">Bundesland</p>
          <p className="font-bold text-blue-900 dark:text-blue-100">{data.state}</p>
        </div>
        <div>
          <p className="text-blue-700 dark:text-blue-300">Datenstatus</p>
          <p className="font-bold text-amber-700 dark:text-amber-300">{data.estimated ? 'nicht amtlich verifiziert (regional abgeleitet)' : 'Quelle ausgewiesen'}</p>
        </div>
      </div>
      {data.estimated && (
        <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
          ℹ️ Kein amtlicher Bodenrichtwert (keine BORIS-Abfrage) – regionaler Proxy nur zur Orientierung, für Rechnungen gesperrt.
        </p>
      )}
    </div>
  );
}

export default CalculationTransparency;

